import React, { useMemo } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";

import {
    selectModelComponents,
    selectHeatingCooling,
    selectDomesticHotWater,
    selectGeneration,
    selectProgram,
    selectAirtightness,
    selectSpecifications,
} from "features/Model/_ducks/selectors";
import { selectAllTags } from "store/imageGallery/selectors";

import TagSection from "./TagsSection";

import styles from "./styles.module.scss";

const RequiredTags = ({
    selectedTag,
    setSelectedTag,
    allImages,
    modelComponents,
    modelHeatingCooling,
    modelDomesticHotWater,
    modelGeneration,
    modelProgram,
    modelAirtightness,
    modelSpecifications,
    requiredTags,
}) => {
    const updatedTags = useMemo(() => {
        const cloneTags = requiredTags.map((tag) => ({ ...tag, isInModel: false }));

        const setIsInModel = (id, value) => {
            const index = cloneTags.findIndex((tag) => tag.id === id);
            if (index !== -1) {
                cloneTags[index].isInModel = value;
            }
        };

        // ------------------- Model Components -------------------
        const { basement = {}, ceiling = {}, crawlspace = {}, expFloor = {} } = modelComponents || {};
        const basementKeys = Object.keys(basement);
        const ceilingKeys = Object.keys(ceiling);
        const crawlspaceKeys = Object.keys(crawlspace);
        const expFloorKeys = Object.keys(expFloor);

        if (basementKeys.length > 0) {
            setIsInModel("basementWall", true);
            if (basementKeys.some((key) => Object.keys(basement[key]?.subcomponents?.floorHeader || {}).length > 0)) {
                setIsInModel("header", true);
            }
        }

        if (crawlspaceKeys.length > 0) {
            setIsInModel("crawlSpace", true);
            if (
                crawlspaceKeys.some((key) => Object.keys(crawlspace[key]?.subcomponents?.floorHeader || {}).length > 0)
            ) {
                setIsInModel("header", true);
            }
        }

        if (expFloorKeys.length > 0) {
            setIsInModel("exposedFloor", true);
        }

        if (ceilingKeys.length > 0) {
            ceilingKeys.forEach((key) => {
                const { constructionType: { id } = {} } = ceiling[key] || {};
                if (id === 0 || id === 1) {
                    setIsInModel("attic", true);
                }
                if (id === 2 || id === 3) {
                    setIsInModel("cathedral&FlatCeilings", true);
                }
            });
        }

        // ------------------- Heating & Cooling -------------------
        const { cooling: { system = {} } = {} } = modelHeatingCooling || {};
        if (Object.keys(system).length > 0) {
            setIsInModel("ashpAndCentralAc", true);
        }

        // ------------------- Domestic Hot Water -------------------
        const { primarySystem = {}, secondarySystem = {} } = modelDomesticHotWater || {};

        if (primarySystem.hasDrainWaterHeatRecovery || secondarySystem.hasDrainWaterHeatRecovery) {
            setIsInModel("dwhr", true);
        }

        if (primarySystem?.energySource?.id === 8 || secondarySystem?.energySource?.id === 8) {
            setIsInModel("solarDHW", true);
        }

        // ------------------- Generation -------------------
        const { pvSystems = {}, windEnergyContribution = 0 } = modelGeneration || {};
        if (Object.keys(pvSystems).length > 0) {
            setIsInModel("pvSolarPanel", true);
        }
        if (windEnergyContribution > 0) {
            setIsInModel("windTurbines", true);
        }

        // ------------------- Program -------------------
        const { options: { main: { atypicalElectricalLoads = false } = {} } = {} } = modelProgram || {};
        if (atypicalElectricalLoads) {
            setIsInModel("atypicalEnergyLoads", true);
        }

        // ------------------- Airtightness -------------------
        const { testType: { id: testTypeId } = {} } = modelAirtightness || {};
        if (testTypeId === 0) {
            setIsInModel("blowerDoor", true);
        }

        // ------------------- Specifications -------------------
        const { numStoreys: { id: numStoreysId } = {} } = modelSpecifications || {};
        if (numStoreysId === 1 || numStoreysId === 3) {
            setIsInModel("halfStorey", true);
        }

        return cloneTags;
    }, [
        requiredTags,
        modelComponents,
        modelHeatingCooling,
        modelDomesticHotWater,
        modelGeneration,
        modelProgram,
        modelAirtightness,
        modelSpecifications,
    ]);

    return (
        <div className={styles.requiredTagsContainer}>
            <TagSection
                selectedTag={selectedTag}
                setSelectedTag={setSelectedTag}
                requiredTags={updatedTags}
                includedTags
                tagsSectionTitle={"REQUIRED TAGS"}
                allImages={allImages}
            />
            <div className={styles.divider} />
            <TagSection
                selectedTag={selectedTag}
                setSelectedTag={setSelectedTag}
                requiredTags={updatedTags}
                includedTags={false}
                customTags={false}
                tagsSectionTitle={"NOT IN CURRENT MODEL"}
                allImages={allImages}
            />
            <div className={styles.divider} />
            <TagSection
                selectedTag={selectedTag}
                setSelectedTag={setSelectedTag}
                requiredTags={updatedTags}
                includedTags={false}
                customTags={true}
                tagsSectionTitle={"CUSTOM TAGS"}
                allImages={allImages}
            />
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    modelComponents: selectModelComponents,
    modelHeatingCooling: selectHeatingCooling,
    modelDomesticHotWater: selectDomesticHotWater,
    modelGeneration: selectGeneration,
    modelProgram: selectProgram,
    modelAirtightness: selectAirtightness,
    modelSpecifications: selectSpecifications,
    requiredTags: selectAllTags,
});

export default connect(mapStateToProps, null)(RequiredTags);
